import React from 'react';
import { Plus, ExternalLink, Edit, Trash2, X } from 'lucide-react';
import { Customer } from '../../../types';
import { CustomerContact } from '../../../types/customer-contact';
import { formatAddress, emptyContactForm } from '../utils/customerHelpers';
import Badge from '../../../components/ui/Badge';
import { userStatusVariant } from '../../../utils/badgeVariants';

type ContactFormState = typeof emptyContactForm;

interface CustomerViewPanelProps {
  selectedCustomer: Customer;
  contacts: CustomerContact[];
  loadingContacts: boolean;
  showContactForm: boolean;
  editingContact: CustomerContact | null;
  contactForm: ContactFormState;
  submittingContact: boolean;
  onContactFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSubmitContact: (e: React.FormEvent) => void;
  onEditContact: (contact: CustomerContact) => void;
  onDeleteContact: (contactId: number) => void;
  onAddContactClick: () => void;
  onCancelContactForm: () => void;
}

const CustomerViewPanel: React.FC<CustomerViewPanelProps> = ({
  selectedCustomer,
  contacts,
  loadingContacts,
  showContactForm,
  editingContact,
  contactForm,
  submittingContact,
  onContactFormChange,
  onSubmitContact,
  onEditContact,
  onDeleteContact,
  onAddContactClick,
  onCancelContactForm,
}) => (
  <div className="space-y-3">
    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-secondary mb-0.5">Customer Name</label>
      <p className="text-sm theme-text-primary font-medium">{selectedCustomer.item}</p>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-xl theme-border theme-card p-3">
        <label className="block text-sm font-medium theme-text-secondary mb-0.5">Client ID</label>
        <p className="text-sm theme-text-primary">{selectedCustomer.client_id}</p>
      </div>
      <div className="rounded-xl theme-border theme-card p-3">
        <label className="block text-sm font-medium theme-text-secondary mb-0.5">Short Name</label>
        <p className="text-sm theme-text-primary">{selectedCustomer.short_name}</p>
      </div>
    </div>

    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-secondary mb-0.5">Monday ID</label>
      <p className="text-sm theme-text-primary">{selectedCustomer.monday_id || '-'}</p>
    </div>

    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-secondary mb-0.5">Funding Type</label>
      <p className="text-sm theme-text-primary">{selectedCustomer.funding_type || '-'}</p>
    </div>

    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-secondary mb-0.5">Status</label>
      <Badge variant={userStatusVariant(selectedCustomer.status)}>{selectedCustomer.status}</Badge>
    </div>

    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-secondary mb-0.5">Client Drive Name</label>
      <p className="text-sm theme-text-primary">{selectedCustomer.client_drive_name || '-'}</p>
    </div>

    <div className="rounded-xl theme-border theme-card p-3">
      <label className="block text-sm font-medium theme-text-secondary mb-0.5">Google Drive URL</label>
      {selectedCustomer.google_drive_url ? (
        <a
          href={selectedCustomer.google_drive_url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open Drive
        </a>
      ) : (
        <p className="text-sm theme-text-tertiary">-</p>
      )}
    </div>

    {/* Contacts Section */}
    <div className="rounded-xl theme-border theme-card p-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold theme-text-primary">Contacts</h3>
        <button
          onClick={onAddContactClick}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-3 h-3" />
          Add Contact
        </button>
      </div>

      {loadingContacts ? (
        <div className="text-center py-3 text-sm theme-text-tertiary">Loading contacts...</div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-3 text-sm theme-text-tertiary">No contacts added yet</div>
      ) : (
        <div className="space-y-2">
          {contacts.map((contact) => (
            <div key={contact.id} className="theme-border rounded p-2 theme-bg-secondary">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <p className="font-medium text-sm theme-text-primary">{contact.name}</p>
                    {contact.is_primary && (
                      <Badge variant="info">Primary</Badge>
                    )}
                  </div>
                  {contact.title && <p className="text-sm theme-text-secondary mb-0.5">{contact.title}</p>}
                  {contact.email && (
                    <p className="text-sm theme-text-secondary mb-0.5">
                      <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800">{contact.email}</a>
                    </p>
                  )}
                  {contact.phone && (
                    <p className="text-sm theme-text-secondary mb-0.5">
                      <a href={`tel:${contact.phone}`} className="text-blue-600 hover:text-blue-800">{contact.phone}</a>
                    </p>
                  )}
                  {formatAddress(contact) !== '-' && (
                    <p className="text-sm theme-text-secondary">{formatAddress(contact)}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onEditContact(contact)}
                    className="p-1 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onDeleteContact(contact.id)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Contact Form */}
    {showContactForm && (
      <div className="rounded-xl theme-border theme-card p-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold theme-text-primary">
            {editingContact ? 'Edit Contact' : 'Add Contact'}
          </h3>
          <button onClick={onCancelContactForm} className="p-0.5 theme-text-tertiary hover:theme-text-primary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={onSubmitContact} className="space-y-2">
          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              name="name"
              value={contactForm.name}
              onChange={onContactFormChange}
              required
              className="w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">Email</label>
              <input
                name="email"
                type="email"
                value={contactForm.email}
                onChange={onContactFormChange}
                className="w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">Phone</label>
              <input
                name="phone"
                value={contactForm.phone}
                onChange={onContactFormChange}
                className="w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Title</label>
            <input
              name="title"
              value={contactForm.title}
              onChange={onContactFormChange}
              className="w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Street</label>
            <input
              name="street"
              value={contactForm.street}
              onChange={onContactFormChange}
              className="w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">City</label>
              <input
                name="city"
                value={contactForm.city}
                onChange={onContactFormChange}
                className="w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">State</label>
              <input
                name="state"
                value={contactForm.state}
                onChange={onContactFormChange}
                className="w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">ZIP</label>
              <input
                name="zip"
                value={contactForm.zip}
                onChange={onContactFormChange}
                className="w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">Country</label>
              <input
                name="country"
                value={contactForm.country}
                onChange={onContactFormChange}
                className="w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Notes</label>
            <textarea
              name="notes"
              value={contactForm.notes}
              onChange={onContactFormChange}
              rows={2}
              className="w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="checkbox"
              name="is_primary"
              checked={contactForm.is_primary}
              onChange={onContactFormChange}
              className="w-3.5 h-3.5 text-blue-600 theme-border rounded focus:ring-blue-500"
            />
            <label className="text-sm font-medium theme-text-primary">Primary Contact</label>
          </div>
          <div className="flex gap-2 pt-1.5">
            <button
              type="button"
              onClick={onCancelContactForm}
              className="flex-1 px-2 py-1 theme-border rounded hover:opacity-80 transition-opacity text-sm theme-bg-secondary theme-text-primary"
              disabled={submittingContact}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submittingContact}
              className="flex-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
            >
              {submittingContact ? 'Saving...' : editingContact ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    )}
  </div>
);

export default CustomerViewPanel;
